/**
 * DocumentsPage Utility Functions
 *
 * This file contains utility functions used by the DocumentsPage component.
 * These functions handle repetitive patterns and complex logic to keep the
 * main component file clean and maintainable.
 */

import { Collection } from '@/types/documents/documents.types';
import { DataRoomFile } from '@/types/documents/documents.types';

// ============= Types =============

/**
 * Collection suggestion from AI analysis.
 */
export interface CollectionSuggestion {
  collectionId?: string;
  collectionName: string;
  description?: string;
  confidence: number;
  reason?: string;
}

/**
 * Collection response from API (can be various formats).
 */
export type CollectionResponse =
  | string
  | { collectionId?: string; id?: string; data?: { collectionId?: string; id?: string } }
  | undefined;

// ============= Constants =============

/**
 * Delay in milliseconds to wait for workspace data refresh after collection creation.
 */
const COLLECTION_REFRESH_DELAY_MS = 500;

/**
 * Maximum length for synopsis preview in UI.
 */
const MAX_SYNOPSIS_PREVIEW_LENGTH = 150;

// ============= Utilities =============

/**
 * Extracts collection ID from a collection object.
 * Handles various ID field names that may exist in the collection object.
 *
 * @param collection - Collection object with potential ID fields
 * @returns Collection ID string or undefined if not found
 */
export const extractCollectionId = (collection: Collection): string | undefined => {
  return collection.id || collection.collectionId;
};

/**
 * Extracts collection ID from a document/file object.
 * Handles various ID field names that may exist in document objects.
 *
 * @param document - Document object with potential collection ID fields
 * @param getFileCollectionId - Function to get collection ID from file ID
 * @returns Collection ID string or undefined if not found
 */
export const extractDocumentCollectionId = (
  document: DataRoomFile | null,
  getFileCollectionId?: (fileId: string) => string | null
): string | null => {
  if (!document) return null;

  // Try helper function first
  if (getFileCollectionId) {
    const id = getFileCollectionId(document.dataRoomFileId);
    if (id) return id;
  }

  // Try various ID field names
  return (
    document.collectionId ||
    document.dataRoomCollectionId ||
    (document as any)?.collectionId ||
    (document as any)?.dataRoomCollectionId ||
    null
  );
};

/**
 * Extracts collection ID from API response.
 * Handles various response formats that the API may return.
 *
 * @param response - API response object (can be string, object, or undefined)
 * @returns Collection ID string or undefined if not found
 */
export const extractCollectionIdFromResponse = (
  response: CollectionResponse
): string | undefined => {
  if (!response) return undefined;

  // If response is a string, it's likely the ID
  if (typeof response === 'string') {
    return response;
  }

  // Try various nested ID fields
  if (response.collectionId) {
    return response.collectionId;
  }

  if (response.id) {
    return response.id;
  }

  if (response.data?.collectionId) {
    return response.data.collectionId;
  }

  if (response.data?.id) {
    return response.data.id;
  }

  return undefined;
};

/**
 * Finds a collection by name in a list of collections.
 * Used to locate newly created collections after refresh.
 *
 * @param collections - Array of collection objects to search
 * @param collectionName - Name of the collection to find
 * @param isSmartCollection - Whether to filter by smart collection flag
 * @returns Found collection or undefined
 */
export const findCollectionByName = (
  collections: Collection[],
  collectionName: string,
  isSmartCollection?: boolean
): Collection | undefined => {
  return collections.find(
    (col: Collection) =>
      (col.name === collectionName || col.title === collectionName) &&
      (isSmartCollection === undefined || col.isSmartCollection === isSmartCollection)
  );
};

/**
 * Truncates text to a maximum length with ellipsis.
 *
 * @param text - Text to truncate
 * @param maxLength - Maximum length before truncation
 * @returns Truncated text with ellipsis if needed
 */
export const truncateText = (text: string, maxLength: number = MAX_SYNOPSIS_PREVIEW_LENGTH): string => {
  if (text.length <= maxLength) {
    return text;
  }
  return `${text.substring(0, maxLength)}...`;
};

/**
 * Formats confidence percentage for display.
 *
 * @param confidence - Confidence value between 0 and 1
 * @returns Formatted percentage string (e.g., "85%")
 */
export const formatConfidence = (confidence: number): string => {
  return `${Math.round(confidence * 100)}%`;
};

/**
 * Gets the collection refresh delay constant.
 *
 * @returns Delay in milliseconds
 */
export const getCollectionRefreshDelay = (): number => {
  return COLLECTION_REFRESH_DELAY_MS;
};

/**
 * Gets collection display name with fallback.
 *
 * @param collection - Collection object
 * @returns Display name or fallback text
 */
export const getCollectionDisplayName = (collection: Collection): string => {
  return collection.name || collection.title || 'Unnamed Collection';
};

/**
 * Gets collection icon based on type.
 *
 * @param isSmartCollection - Whether the collection is a smart collection
 * @returns Icon emoji string
 */
export const getCollectionIcon = (isSmartCollection?: boolean): string => {
  return isSmartCollection ? '✨' : '📁';
};

/**
 * Checks if a document is in a specific collection.
 *
 * @param document - Document to check
 * @param collectionId - Collection ID to check against
 * @param getFileCollectionId - Function to get collection ID from file ID
 * @returns True if document is in the collection
 */
export const isDocumentInCollection = (
  document: DataRoomFile | null,
  collectionId: string | null | undefined,
  getFileCollectionId?: (fileId: string) => string | null
): boolean => {
  if (!document || !collectionId) return false;

  const docCollectionId = extractDocumentCollectionId(document, getFileCollectionId);
  return docCollectionId === collectionId;
};
